import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import {
  DEFAULT_ORDER_PICKUP_TIME,
  FULFILLMENT_METHOD_TYPES,
  VERTICALS
} from "../../utils/constants";
import { Formik } from "formik";
import { add } from "date-fns";
import { yyyyMMdd } from "../../utils/helper";
import HomeCareBulkOrderCreateWrapper from "./homecare-bulk-order-create-wrapper";

const HOMECARE_PRESCRIPTION_ACCEPT =
  ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

const homeCarePrescriptionFileOk = file => {
  if (!file || !(file instanceof File)) return { ok: true };
  const maxBytes = 10 * 1024 * 1024;
  if (file.size > maxBytes) {
    return { ok: false, message: "Prescription must be at most 10 MB." };
  }
  const nameOk = /\.(pdf|jpe?g|png)$/i.test(file.name);
  const typeOk = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/jpg",
    ""
  ].includes(file.type);
  if (nameOk || typeOk) return { ok: true };
  return {
    ok: false,
    message: "Prescription must be a PDF, JPEG, or PNG file."
  };
};

const makeScheduleRow = () => ({
  key: `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  pickupDate: add(new Date(), { minutes: DEFAULT_ORDER_PICKUP_TIME }),
  timeSlotId: ""
});

const HomeCareBulkOrderCreate = props => {
  const {
    customerIds,
    customersLoading,
    getCustomersId,
    getCustomerDetail,
    customerDetail,
    history,
    resetCustomerState,
    scanTypes,
    getOrderScanTypes,
    scanTypesLoading,
    resetScanTypesState,
    cities,
    createCustomerAddress,
    createCustomerPatient,
    updateCustomerPatient,
    deleteCustomerPatient,
    createCustomer,
    createHomeCareBulkOrders,
    ordersLoading,
    HomeCareOrderParams,
    deleteCustomerAddress,
    hearAboutUsList,
    getHearAboutUsList,
    resetOrderTimeSlotsState,
    resetCopyOrder,
    copiedOrder,
    getCities,
    regions,
    getOrderSources,
    orderSources,
    homeCareCategories,
    getHomeCareCategories
  } = props;

  const [scheduleRows, setScheduleRows] = useState([makeScheduleRow()]);
  const [slotsByRow, setSlotsByRow] = useState({});
  const [slotsLoadingByRow, setSlotsLoadingByRow] = useState({});

  useEffect(() => {
    getCities({ vertical: VERTICALS.homecare });
  }, []);

  const homeCarePrescriptionFileRef = useRef(null);

  const onDiscard = e => {
    e.preventDefault();
    homeCarePrescriptionFileRef.current = null;
    resetScanTypesState();
    resetOrderTimeSlotsState();
    resetCopyOrder();
    setScheduleRows([makeScheduleRow()]);
    setSlotsByRow({});
    setSlotsLoadingByRow({});
    history.goBack();
  };

  const orderInitialValues = {
    patient: "",
    value: "",
    discount: "",
    additionalNotes: "",
    scanType: [],
    urgentDelivery: false,
    requiresFemaleStaff: false,
    customerAddress: "",
    city: "",
    hearAbout: null,
    fulfillmentMethod: FULFILLMENT_METHOD_TYPES.ONLINE,
    orderSource: null,
    homeCareCategory: null,
    region: "",
    prescriptionFile: null,
    estimatedTime: "",
    hasAllergy: false,
    allergies: ""
  };

  return (
    <div id="wrapper">
      <Formik
        initialValues={orderInitialValues}
        validationSchema={Yup.object().shape({
          fulfillmentMethod: Yup.number().required("Please select order type"),
          patient: Yup.number().required(),
          discount: Yup.number()
            .min(0, "Order discount can not be less than 0")
            .max(100, "Order discount be less than or equal to 100")
            .typeError("Invalid order discount"),
          value: Yup.number()
            .min(0, "Order value can not be less than 0")
            .typeError("Invalid order value"),
          additionalNotes: Yup.string().max(
            1000,
            "Additional notes must be at most 1000 characters"
          ),
          scanType: Yup.array()
            .of(Yup.number())
            .min(1, "Select Test Type")
            .required(),
          orderSource: Yup.number().required("Please select order source"),
          homeCareCategory: Yup.number().required(
            "Please select home care category"
          ),
          prescriptionFile: Yup.mixed()
            .nullable()
            .test("prescription-type", "", function (value) {
              const file =
                homeCarePrescriptionFileRef.current instanceof File
                  ? homeCarePrescriptionFileRef.current
                  : value;
              const r = homeCarePrescriptionFileOk(file);
              if (r.ok) return true;
              return this.createError({ message: r.message });
            })
        })}
        onSubmit={values => {
          const data = { ...values };
          const prescriptionFile =
            homeCarePrescriptionFileRef.current instanceof File
              ? homeCarePrescriptionFileRef.current
              : data.prescriptionFile instanceof File
                ? data.prescriptionFile
                : null;
          delete data.prescriptionFile;
          homeCarePrescriptionFileRef.current = null;
          if (!(data.value > 0)) {
            delete data.value;
          }
          delete data["discount"];
          delete data["city"];
          delete data["region"];
          const schedule = scheduleRows.map(row => ({
            date: yyyyMMdd(row.pickupDate),
            timeSlot: String(row.timeSlotId)
          }));
          const payload = {
            patient: data.patient,
            customerAddress: data.customerAddress,
            scanType: data.scanType,
            fulfillmentMethod: data.fulfillmentMethod,
            orderSource: data.orderSource,
            homeCareCategory: data.homeCareCategory,
            urgentDelivery: data.urgentDelivery,
            requiresFemaleStaff: data.requiresFemaleStaff,
            additionalNotes: data.additionalNotes,
            hearAbout: data.hearAbout,
            estimatedTime: data.estimatedTime,
            hasAllergy: data.hasAllergy,
            allergies: data.hasAllergy ? data.allergies : "",
            vertical: VERTICALS.homecare,
            isExternal: false,
            schedule,
            homeCarePrescriptionFile: prescriptionFile || undefined
          };
          if (data.value > 0) {
            payload.value = data.value;
          }
          createHomeCareBulkOrders({
            orderParams: HomeCareOrderParams,
            data: payload
          });
        }}
      >
        {orderFormik => (
          <HomeCareBulkOrderCreateWrapper
            orderFormik={orderFormik}
            scheduleRows={scheduleRows}
            setScheduleRows={setScheduleRows}
            slotsByRow={slotsByRow}
            setSlotsByRow={setSlotsByRow}
            slotsLoadingByRow={slotsLoadingByRow}
            setSlotsLoadingByRow={setSlotsLoadingByRow}
            customerIds={customerIds}
            customersLoading={customersLoading}
            getCustomersId={getCustomersId}
            getCustomerDetail={getCustomerDetail}
            customerDetail={customerDetail}
            history={history}
            resetCustomerState={resetCustomerState}
            createCustomerPatient={createCustomerPatient}
            updateCustomerPatient={updateCustomerPatient}
            deleteCustomerPatient={deleteCustomerPatient}
            onDiscard={onDiscard}
            createCustomer={createCustomer}
            ordersLoading={ordersLoading}
            orderInitialValues={orderInitialValues}
            copiedOrder={copiedOrder}
            createCustomerAddress={createCustomerAddress}
            cities={cities}
            deleteCustomerAddress={deleteCustomerAddress}
            scanTypes={scanTypes}
            getOrderScanTypes={getOrderScanTypes}
            scanTypesLoading={scanTypesLoading}
            resetScanTypesState={resetScanTypesState}
            hearAboutUsList={hearAboutUsList}
            getHearAboutUsList={getHearAboutUsList}
            regions={regions}
            getOrderSources={getOrderSources}
            orderSources={orderSources}
            homeCareCategories={homeCareCategories}
            getHomeCareCategories={getHomeCareCategories}
            prescriptionAccept={HOMECARE_PRESCRIPTION_ACCEPT}
            prescriptionFileValidate={homeCarePrescriptionFileOk}
            homeCarePrescriptionFileRef={homeCarePrescriptionFileRef}
          />
        )}
      </Formik>
    </div>
  );
};
export default HomeCareBulkOrderCreate;
